import {
  IndexedDBDocStorage,
  IndexedDBDocSyncStorage,
} from '@affine/nbstore/idb';
import { SqliteDocStorage, SqliteDocSyncStorage } from '@affine/nbstore/sqlite';
import type { StoreClient } from '@affine/nbstore/worker/client';
import { Entity } from '@toeverything/infra';

import type { ServerService } from '../../cloud';
import { LocalAccountKeys } from '../../local-data-keys/local-account-keys';
import type { NbstoreService } from '../../storage';

export class UserDBEngine extends Entity<{
  userId: string;
}> {
  private readonly userId = this.props.userId;
  readonly client: StoreClient;

  DocStorageType =
    BUILD_CONFIG.isElectron || BUILD_CONFIG.isIOS || BUILD_CONFIG.isAndroid
      ? SqliteDocStorage
      : IndexedDBDocStorage;
  DocSyncStorageType =
    BUILD_CONFIG.isElectron || BUILD_CONFIG.isIOS || BUILD_CONFIG.isAndroid
      ? SqliteDocSyncStorage
      : IndexedDBDocSyncStorage;

  canGracefulStop() {
    // TODO(@eyhn): Implement this
    return true;
  }

  constructor(
    private readonly nbstoreService: NbstoreService,
    serverService: ServerService
  ) {
    super();

    const { store, dispose } = this.nbstoreService.openStore(
      `userspace:${serverService.server.id},${this.userId}`,
      this.framework.get(LocalAccountKeys).resolveStoreOptions(
        {
          local: {
            doc: {
              name: this.DocStorageType.identifier,
              opts: {
                type: 'userspace',
                flavour: serverService.server.id,
                id: this.userId,
              },
            },
            docSync: {
              name: this.DocSyncStorageType.identifier,
              opts: {
                type: 'userspace',
                flavour: serverService.server.id,
                id: this.userId,
              },
            },
          },
          // Userspace is device-local only — no cloud remote.
          //
          // The backend has no server-side userspace doc store: `docs` and
          // `doc_snapshots` are FK-bound to `workspaces`, and there is no
          // per-user snapshot table. A `remotes.cloud` target here therefore
          // only emitted failing `space:push-doc-update userspace/<userId>/...`
          // pushes — the workspace doc-persist producer (`doc_persist_enqueue`)
          // rejects them with 42501 because a userspace has no `workspace_members`
          // row. That failing push was the trigger behind the 2026-05-30 backend
          // crash.
          //
          // Editor settings (the only userspace collection) persist locally in
          // IndexedDB/Sqlite and are device-scoped by design. Re-introduce a
          // cloud remote here ONLY together with a real server-side userspace
          // store (own tables keyed by userId, userspace-aware producer + worker),
          // never on its own.
          remotes: {},
        },
        { flavour: serverService.server.id, workspaceId: this.userId }
      )
    );
    this.client = store;
    this.client.docFrontend.start();
    this.disposables.push(() => dispose());
  }
}
